import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { RoomDetail } from '@/constants/rooms'

import Image from 'next/image'
import {
    BedDouble,
    Maximize,
    Users,
    Star
} from 'lucide-react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PillButton } from '@/components/generals/pill-button'
import { UnderlineLink } from '@/components/generals/underline-link'
import { Choose } from '@/components/home/choose'
import { roomDetails, roomAmenities, getRoom } from '@/constants/rooms'

interface RoomPageProps {
    room: RoomDetail
}

const eyebrow = 'text-[12px] font-medium uppercase tracking-[0.16em] text-ink-soft'

const RoomPage: FC<RoomPageProps> = ({ room }): ReactNode => {
    return (
        <Layout>
            <Seo
                title={room.seoTitle}
                description={room.seoDescription}
            />

            <header className='relative flex min-h-[80vh] items-end overflow-hidden pb-12 md:min-h-[90vh] md:pb-16'>
                <div className='absolute inset-0'>
                    <div className='absolute left-0 top-0 h-full w-full' data-parallax='30'>
                        <Image
                            src={room.heroImage}
                            alt={room.heroImageAlt}
                            fill
                            sizes='100vw'
                            className='object-cover'
                            priority
                        />
                    </div>

                    <div aria-hidden='true' className='absolute inset-0 bg-ink/35' />
                </div>

                <div className='shell relative flex flex-col items-center gap-8'>
                    <h1
                        className='max-w-[900px] text-center font-heading text-[54px] font-medium leading-[0.95] tracking-[-0.04em] text-cream md:text-[80px] lg:text-[104px]'
                        data-reveal='up'
                        data-reveal-load
                        data-reveal-delay='0.4'
                    >
                        {room.name}
                    </h1>

                    <div
                        className='flex flex-wrap items-center justify-center gap-2.5'
                        data-reveal='up'
                        data-reveal-load
                        data-reveal-delay='0.8'
                    >
                        {[
                            { icon: <BedDouble className='size-4' aria-hidden='true' />, label: room.bed },
                            { icon: <Users className='size-4' aria-hidden='true' />, label: room.guests },
                            { icon: <Maximize className='size-4' aria-hidden='true' />, label: room.size }
                        ].filter((chip) => chip.label).map((chip) => (
                            <span
                                key={chip.label}
                                className='inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-cream px-5 py-3 text-[15px] font-medium text-ink md:text-[16px]'
                            >
                                {chip.icon}
                                {chip.label}
                            </span>
                        ))}
                    </div>
                </div>
            </header>

            <section className='shell py-20 md:py-28 lg:py-32'>
                <div className='grid gap-12 lg:grid-cols-2 lg:gap-16'>
                    <div data-reveal='up'>
                        <p className={eyebrow}>
                            Overview
                        </p>

                        <div className='mt-7 space-y-6'>
                            <p className='t-h4'>
                                The{' '}

                                <em>
                                    {room.name}
                                </em>{' '}

                                {room.overview[0]}
                            </p>

                            {room.overview.slice(1).map((paragraph) => (
                                <p key={paragraph} className='t-h4'>
                                    {paragraph}
                                </p>
                            ))}
                        </div>

                        <PillButton href='/contact' className='mt-9'>
                            Reserve now
                        </PillButton>
                    </div>

                    <div
                        className='relative aspect-[4/3] overflow-hidden rounded-[10px] bg-cream-deep lg:aspect-auto lg:self-stretch'
                        data-reveal='up'
                    >
                        <div className='absolute left-0 top-0 h-full w-full' data-parallax='20'>
                            <Image
                                src={room.overviewImage}
                                alt={room.overviewImageAlt}
                                fill
                                sizes='(max-width: 1024px) 100vw, 45vw'
                                className='object-cover'
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className='relative h-[46vh] min-h-[320px] overflow-hidden md:h-[70vh]'>
                <div className='absolute left-0 top-0 h-full w-full' data-parallax='30'>
                    <Image
                        src={room.galleryImage}
                        alt={room.galleryImageAlt}
                        fill
                        sizes='100vw'
                        className='object-cover'
                    />
                </div>
            </section>

            <section className='shell py-20 md:py-28 lg:py-32'>
                <div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
                    <h2 className='t-h1' data-reveal='up'>
                        <span className='block'>
                            Room
                        </span>

                        <em className='block'>
                            Pricing
                        </em>
                    </h2>

                    <div className='max-w-[420px] lg:text-right' data-reveal='up' data-reveal-delay='0.1'>
                        <p className='t-h5'>
                            {room.pricingTitle}
                        </p>

                        <p className='t-body mt-3'>
                            {room.pricingSubtitle}
                        </p>
                    </div>
                </div>

                <div className='mt-12 grid gap-5 lg:grid-cols-2'>
                    <div
                        className='flex flex-col justify-center rounded-[10px] border border-hairline bg-cream-soft p-8 text-center md:p-12'
                        data-reveal='up'
                    >
                        <p className={eyebrow}>
                            Rates
                        </p>

                        <p className='mt-6 font-heading text-[44px] font-medium leading-none tracking-[-0.03em] text-brand md:text-[64px]'>
                            <em>
                                {room.price}
                            </em>
                        </p>

                        <p className='t-body mx-auto mt-5 max-w-[320px]'>
                            {room.priceIncludes}
                        </p>

                        <div className='mt-7 flex justify-center'>
                            <UnderlineLink href='/contact' className='text-brand'>
                                Reserve now
                            </UnderlineLink>
                        </div>
                    </div>

                    <div
                        className='flex flex-col rounded-[10px] border border-hairline bg-cream-soft p-6 md:p-8'
                        data-reveal='up'
                        data-reveal-delay='0.1'
                    >
                        <p className={eyebrow}>
                            {room.addOnsTitle}
                        </p>

                        <ul className='mt-5 flex flex-col'>
                            {room.addOns.map((addOn) => (
                                <li
                                    key={addOn.label}
                                    className='flex items-center justify-between gap-4 border-b border-hairline py-4 last:border-b-0'
                                >
                                    <span className='t-label'>
                                        {addOn.label}
                                    </span>

                                    <span className='t-label text-ink-soft'>
                                        {addOn.price}
                                    </span>
                                </li>
                            ))}
                        </ul>

                        <PillButton href='/packages' className='mt-7 w-full'>
                            See ski &amp; stay packages
                        </PillButton>
                    </div>
                </div>
            </section>

            <section className='shell pb-20 md:pb-28 lg:pb-32'>
                <div className='grid gap-5 lg:grid-cols-2'>
                    <div
                        className='relative flex min-h-[420px] flex-col justify-end overflow-hidden rounded-[10px] bg-ink p-8 md:min-h-[560px] md:p-12'
                        data-reveal='up'
                    >
                        <div className='absolute inset-0'>
                            <div className='absolute left-0 top-0 h-full w-full' data-parallax='20'>
                                <Image
                                    src={room.featuresImage}
                                    alt=''
                                    fill
                                    sizes='(max-width: 1024px) 100vw, 50vw'
                                    className='object-cover'
                                />
                            </div>

                            <div aria-hidden='true' className='absolute inset-0 bg-ink/55' />
                        </div>

                        <div className='relative'>
                            <p className='text-[12px] font-medium uppercase tracking-[0.16em] text-cream/70'>
                                At the Greenbrier
                            </p>

                            <h2 className='t-h1 mt-5 text-cream'>
                                <span className='block'>
                                    Features
                                </span>

                                <em className='block'>
                                    &amp; Amenities
                                </em>
                            </h2>

                            <p className='t-body mt-5 text-cream/80'>
                                Room and hotel amenities at the Greenbrier Inn
                            </p>
                        </div>
                    </div>

                    <div
                        className='rounded-[10px] border border-hairline bg-cream-soft p-6 md:p-8'
                        data-reveal='up'
                        data-reveal-delay='0.1'
                    >
                        <ul
                            className='grid gap-x-6 sm:grid-cols-2'
                            data-reveal-group
                            data-reveal-stagger='0.05'
                        >
                            {roomAmenities.map((amenity) => (
                                <li
                                    key={amenity}
                                    className='flex items-center gap-3 border-b border-hairline py-4'
                                    data-reveal='up'
                                    data-reveal-distance='10'
                                >
                                    <Star aria-hidden='true' className='size-4 shrink-0 fill-brand text-brand' />

                                    <span className='t-label'>
                                        {amenity}
                                    </span>
                                </li>
                            ))}
                        </ul>

                        <PillButton href='/contact' className='mt-8 w-full'>
                            Reserve now
                        </PillButton>
                    </div>
                </div>
            </section>

            {/* Shown only once real guest reviews are added to the room's data. */}
            {room.reviews.length > 0 && (
                <section className='shell pb-20 md:pb-28 lg:pb-32'>
                    <div className='flex flex-col items-center text-center' data-reveal='up'>
                        <p className={eyebrow}>
                            Guest reviews
                        </p>

                        <p className='mt-5 font-heading text-[80px] font-medium leading-none tracking-[-0.04em] text-brand md:text-[120px]'>
                            {room.rating}
                        </p>

                        <p className='t-body mt-4 max-w-[420px]'>
                            What guests say about the {room.name}.
                        </p>
                    </div>

                    <div
                        className='mt-14 grid grid-cols-2 gap-8 lg:grid-cols-4'
                        data-reveal-group
                        data-reveal-stagger='0.08'
                    >
                        {room.scores.map((score) => (
                            <div key={score.label} className='text-center' data-reveal='up'>
                                <p className='font-heading text-[40px] font-medium leading-none text-brand md:text-[52px]'>
                                    {score.value}
                                </p>

                                <p className={`${eyebrow} mt-3`}>
                                    {score.label}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div
                        className='mt-14 grid gap-5 md:grid-cols-3'
                        data-reveal-group
                        data-reveal-stagger='0.12'
                    >
                        {room.reviews.map((review) => (
                            <figure
                                key={review.name}
                                className='flex flex-col rounded-[10px] border border-hairline bg-cream-soft p-6'
                                data-reveal='up'
                            >
                                <blockquote className='t-h5 flex-1 text-ink'>
                                    {review.quote}
                                </blockquote>

                                <div aria-hidden='true' className='mt-6 flex gap-[3px] text-brand'>
                                    {[0, 1, 2, 3, 4].map((star) => (
                                        <Star key={star} className='size-3.5 fill-current' />
                                    ))}
                                </div>

                                <figcaption className='mt-4 flex items-center gap-3'>
                                    {review.avatar && (
                                        <span className='relative size-10 overflow-hidden rounded-full'>
                                            <Image
                                                src={review.avatar}
                                                alt=''
                                                fill
                                                sizes='40px'
                                                className='object-cover'
                                            />
                                        </span>
                                    )}

                                    <span className='t-label'>
                                        {review.name}
                                    </span>
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                </section>
            )}

            <Choose />
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => ({
    paths: roomDetails.map((room) => ({ params: { slug: room.slug } })),
    fallback: false
})

export const getStaticProps: GetStaticProps<RoomPageProps> = async ({ params }) => {
    const room = getRoom(String(params?.slug))

    if (!room) return { notFound: true }

    return { props: { room } }
}

export default RoomPage
